import React from 'react';
import {
  Navigate,
  Outlet,
  Route as WebRoute,
  Routes as WebRoutes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import routes from './routes';

/* eslint-disable import/order */
/* Unauthenticated */
import SignInScreen from 'web/screens/Unauthenticated/SignInScreen';
import BillSettlementUnauthenticatedScreen from 'web/screens/Unauthenticated/BillSettlementScreen';
import BillsScreen from 'web/screens/Unauthenticated/BillsScreen/BillsScreen';

/* Authenticated */
import RootScreen from 'web/screens/Authenticated/RootScreen';
import HomeScreen from 'web/screens/Authenticated/Home/HomeScreen';
import BillSummaryScreen from 'web/screens/Authenticated/Home/BillSummaryScreen';
import BillSettlementAuthenticatedScreen from 'web/screens/Authenticated/Home/BillSettlementScreen';
import BankTransferScreen from 'web/screens/Authenticated/Home/BankTransferScreen';
import UsageScreen from 'web/screens/Authenticated/Usage/UsageScreen';
import PlusContractsScreen from 'web/screens/Authenticated/Usage/PlusContractsScreen';
import PlusContractDetailScreen from 'web/screens/Authenticated/Usage/PlusContractDetailScreen';
import MultiPayContractsScreen from 'web/screens/Authenticated/Usage/MultiPayContractsScreen';
import MultiPayContractDetailScreen from 'web/screens/Authenticated/Usage/MultiPayContractDetailScreen';
import AccountScreen from 'web/screens/Authenticated/Account/AccountScreen';

/* Common */
import ImpersonateScreen from 'web/screens/Common/ImpersonateScreen';
import NotFoundScreen from 'web/screens/Common/NotFoundScreen';
import ContractsSummaryScreen from 'web/screens/Authenticated/Usage/ContractsSummaryScreen';
import EKYCScreen from 'web/screens/Authenticated/EKYC/EKYCScreen';
import EKYCApplyScreen from 'web/screens/Authenticated/EKYC/EKYCApplyScreen';
/* eslint-enable import/order */

const ExternalURLRedirect = (props: { to: string }) => {
  const [searchParams] = useSearchParams();
  const toSearchParam = searchParams.get('to');

  window.location.replace(toSearchParam || props.to);

  return null;
};

const CommonRoutes = (
  <>
    {/* These routes are used by the mobile app */}
    <WebRoute path={routes.common.apple.root} element={<NotFoundScreen />} />
    <WebRoute
      path={routes.common.apple.feature}
      element={<ExternalURLRedirect to={process.env.PAIDYCOM_URL} />}
    />
    <WebRoute
      path={routes.common.shopping.browse}
      element={<ExternalURLRedirect to={`${process.env.PAIDYCOM_URL}/shop`} />}
    />
    <WebRoute
      path={routes.common.shopping.inspiration}
      element={<ExternalURLRedirect to={`${process.env.PAIDYCOM_URL}/shop`} />}
    />
    <WebRoute
      path={routes.common.paidyCard.onboarding}
      element={<ExternalURLRedirect to={process.env.PAIDYCOM_URL} />}
    />
    <WebRoute
      path={routes.common.paidyCard.paypal}
      element={<ExternalURLRedirect to={process.env.PAIDYCOM_URL} />}
    />
    <WebRoute
      path={routes.common.paidyCard.paypalLink}
      element={
        <ExternalURLRedirect
          to={`${process.env.APP_SCHEME}://links-paypal${window.location.search}`}
        />
      }
    />
    <WebRoute
      path={routes.common.externalRedirect}
      element={<ExternalURLRedirect to="" />}
    />
    {/*NLP deep links*/}
    <WebRoute
      path={routes.common.nlp.repacOnboarding}
      element={<ExternalURLRedirect to={process.env.PAIDYCOM_URL} />}
    />
    <WebRoute
      path={routes.common.nlp.monthlyPowerReset}
      element={<ExternalURLRedirect to={process.env.PAIDYCOM_URL} />}
    />
    <WebRoute
      path={routes.common.nlp.nlpIntro}
      element={<ExternalURLRedirect to={process.env.PAIDYCOM_URL} />}
    />
    {/* Impersonation */}
    <WebRoute
      path={routes.common.impersonate}
      element={<ImpersonateScreen />}
    />
    <WebRoute
      path={routes.common.deepLink.repac}
      element={
        <ExternalURLRedirect
          to={`${process.env.FIREBASE_APP_URL}/${
            process.env.TARGET_ENV === 'staging' ? 'oRqJ' : 'vjDm'
          }`}
        />
      }
    />
    {/* 404 */}
    <WebRoute path={routes.common.notFound} element={<NotFoundScreen />} />
  </>
);

const UnauthenticatedRoutes = () => {
  const location = useLocation();

  // Create an array of authenticated paths that we want to redirect to SignIn screen.
  // Basically all the paths registered in AuthenticatedRoutes.
  const authenticatedPaths: string[] = [];

  Object.values(routes.authenticated).forEach((item) => {
    Object.values(item).map((url) => authenticatedPaths.push(url));
  });

  // Show the SignIn screen that redirects to a certain path on success
  // The path to redirect to is stored in the History state, and it can be
  // retrieved by the SignIn screen.
  const SignInWithRedirection = () => (
    <Navigate
      to={routes.unauthenticated.root}
      state={{ next: location.pathname }}
      replace
    />
  );

  return (
    <WebRoutes>
      <WebRoute
        path={routes.unauthenticated.signIn}
        element={<Navigate to={routes.unauthenticated.root} replace />}
      />
      <WebRoute
        path={routes.unauthenticated.logIn}
        element={<Navigate to={routes.unauthenticated.root} replace />}
      />
      <WebRoute path={routes.unauthenticated.root} element={<SignInScreen />} />
      <WebRoute
        path={routes.unauthenticated.phoneReset}
        element={
          <Navigate to={`${routes.unauthenticated.root}?reset=1`} replace />
        }
      />
      <WebRoute
        path={routes.unauthenticated.bill}
        element={<BillSettlementUnauthenticatedScreen />}
      />
      <WebRoute
        path={routes.unauthenticated.billSettlement}
        element={<BillsScreen />}
      />
      {/* If users go to these authenticated routes redirect them to the SignIn screen */}
      {authenticatedPaths.map((path, index) => (
        <WebRoute path={path} element={<SignInWithRedirection />} key={index} />
      ))}
      {/* Common */}
      {CommonRoutes}
    </WebRoutes>
  );
};

const AuthenticatedRoutes = (
  <WebRoutes>
    <WebRoute element={<RootScreen />}>
      <WebRoute
        index
        element={<Navigate to={routes.authenticated.home.root} replace />}
      />
      <WebRoute path={routes.authenticated.home.root} element={<Outlet />}>
        <WebRoute index element={<HomeScreen />} />
        <WebRoute
          path={routes.authenticated.home.billSummary}
          element={<BillSummaryScreen />}
        />
        <WebRoute
          path={routes.authenticated.home.billDetails}
          element={<BillSummaryScreen />}
        />
        <WebRoute
          path={routes.authenticated.home.billSettlement}
          element={<BillSettlementAuthenticatedScreen />}
        />
        <WebRoute
          path={routes.authenticated.home.billSettlementMethod}
          element={<BillSettlementAuthenticatedScreen />}
        />
        <WebRoute
          path={routes.authenticated.home.billSettlementBankTransfer}
          element={<BankTransferScreen />}
        />
      </WebRoute>
      <WebRoute path={routes.authenticated.usage.root} element={<Outlet />}>
        <WebRoute index element={<UsageScreen />} />
        <WebRoute
          path={routes.authenticated.usage.plusContracts}
          element={<PlusContractsScreen />}
        />
        <WebRoute
          path={routes.authenticated.usage.plusContractDetail}
          element={<PlusContractDetailScreen />}
        />
        <WebRoute
          path={routes.authenticated.usage.multiPayContracts}
          element={<MultiPayContractsScreen />}
        />
        <WebRoute
          path={routes.authenticated.usage.contractsSummary}
          element={<ContractsSummaryScreen />}
        />
        <WebRoute
          path={routes.authenticated.usage.multiPayContractDetail}
          element={<MultiPayContractDetailScreen />}
        />
      </WebRoute>
      <WebRoute path={routes.authenticated.account.root} element={<Outlet />}>
        <WebRoute index element={<AccountScreen />} />
      </WebRoute>
    </WebRoute>
    {/* EKYC */}
    <WebRoute path={routes.authenticated.ekyc.root} element={<Outlet />}>
      <WebRoute index element={<EKYCScreen />} />
      <WebRoute
        path={routes.authenticated.ekyc.apply}
        element={<EKYCApplyScreen />}
      />
    </WebRoute>
    {/* If users go to these unauthenticated routes while already logged in, just redirect them to these authenticated routes */}
    <WebRoute
      path={routes.unauthenticated.signIn}
      element={<Navigate to={routes.authenticated.home.root} replace />}
    />
    <WebRoute
      path={routes.unauthenticated.logIn}
      element={<Navigate to={routes.authenticated.home.root} replace />}
    />
    <WebRoute
      path={routes.unauthenticated.phoneReset}
      element={<Navigate to={routes.authenticated.account.root} replace />}
    />
    {/* Display unauthenticated bill settlement screen even if logged in */}
    <WebRoute
      path={routes.unauthenticated.billSettlement}
      element={<BillSettlementUnauthenticatedScreen />}
    />
    {/* Common */}
    {CommonRoutes}
  </WebRoutes>
);

const Navigation = (props: { authenticated: boolean }) => {
  return props.authenticated ? AuthenticatedRoutes : UnauthenticatedRoutes();
};

export default Navigation;
